// Reloading open pages, either because a site admin asked from /admin or because the server
// now hands out a newer build of the web app than the one this page is running.
//
// The server's version is the path of the app's script in its index.html, such as
// /assets/index-3f9a1c.js (src/helpers/siteVersion.ts on the server). This page's own is the
// same path, read off the <script> tag that loaded it, so the two compare directly. In
// development neither has one, and nothing reloads.
//
// A TV reloads after a random few seconds, so a room full of them does not ask the server
// for the page all at once, and its song picks up where it was (utils/PlaybackResume.ts).
// A phone waits until it is put away or switched from, so nobody loses a half-typed search;
// it comes back on the new page.

export type ScreenKind = 'tv' | 'phone';

/** The app script this page was loaded with, such as /assets/index-3f9a1c.js, or null. */
export const pageVersion = (): string | null => {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const script of scripts) {
    const src = script.getAttribute('src') || '';
    const path = src.replace(/^https?:\/\/[^/]+/, '');
    if (/^\/assets\/[^/]+\.js$/.test(path)) {
      return path;
    }
  }
  return null;
};

/** Whether the server's build differs from this page's, when both are known. */
export const isOutdated = (serverVersion: string | null, ownVersion: string | null = pageVersion()) =>
  !!serverVersion && !!ownVersion && serverVersion !== ownVersion;

// A page whose fresh copy still comes back old (a cache somewhere in between) would
// otherwise reload on every reconnect. One reload for a new version per five minutes.
const VERSION_RELOAD_KEY = 'jookable.versionReloadAt';
const VERSION_RELOAD_GAP_MS = 5 * 60 * 1000;

const recentlyReloadedForVersion = () => {
  try {
    const at = Number(window.sessionStorage.getItem(VERSION_RELOAD_KEY) || 0);
    return Date.now() - at < VERSION_RELOAD_GAP_MS;
  } catch (e) {
    return false;
  }
};

const noteVersionReload = () => {
  try {
    window.sessionStorage.setItem(VERSION_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    // Private mode and the like. The worst case is one extra reload.
  }
};

let pending = false;

/**
 * Reload this page: a TV after a random wait up to `spreadMs`, a phone the next time it is
 * hidden. Asking twice does nothing more.
 */
export const scheduleReload = (kind: ScreenKind, spreadMs: number, reason: 'admin' | 'version') => {
  if (pending) {
    return;
  }
  if (reason === 'version') {
    if (recentlyReloadedForVersion()) {
      return;
    }
    noteVersionReload();
  }
  pending = true;

  const reload = () => window.location.reload();

  if (kind === 'tv') {
    window.setTimeout(reload, 1000 + Math.floor(Math.random() * Math.max(0, spreadMs)));
    return;
  }

  if (document.hidden) {
    reload();
    return;
  }
  const whenHidden = () => {
    if (document.hidden) {
      document.removeEventListener('visibilitychange', whenHidden);
      reload();
    }
  };
  document.addEventListener('visibilitychange', whenHidden);
};
